import React, { useState } from 'react';
import useStore from '../store/useStore';
import { alertsAPI } from '../services/api';
import '../styles/Alerts.css';

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 };

export const describeAlert = (alert) => {
  const subject = alert.kind === 'live' ? alert.metricKey : alert.measure;
  const value = alert.lastValue === null || alert.lastValue === undefined ? '—' : Number(alert.lastValue).toLocaleString();
  return `${subject} is ${value} (${alert.operator} ${Number(alert.threshold).toLocaleString()})`;
};

/**
 * Active alerts for the current company, worst severity first. Rendered at
 * the top of the dashboard in desk mode and as a slim strip in wall mode.
 */
const AlertBanner = ({ compact = false }) => {
  const alerts = useStore((state) => state.alerts);
  const applyAlertEvent = useStore((state) => state.applyAlertEvent);
  const currentCompany = useStore((state) => state.currentCompany);
  const user = useStore((state) => state.user);
  const [busy, setBusy] = useState(null);

  if (!alerts || alerts.length === 0) return null;
  const sorted = [...alerts].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3));
  const worst = sorted[0].severity;

  const acknowledge = async (alert) => {
    try {
      setBusy(alert.id);
      const res = await alertsAPI.acknowledge(alert.id, currentCompany?.id);
      if (res?.success) applyAlertEvent({ event: 'acknowledged', rule: res.data });
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`alert-banner alert-banner--${worst} ${compact ? 'alert-banner--compact' : ''}`} role="status" aria-live="polite" data-testid="alert-banner">
      <div className="alert-banner-title">
        <span className="alert-banner-icon" aria-hidden="true">⚠</span>
        <strong>{sorted.length} active alert{sorted.length === 1 ? '' : 's'}</strong>
      </div>
      <ul className="alert-banner-list">
        {sorted.map((alert) => (
          <li key={alert.id} className={`alert-item alert-item--${alert.severity} ${alert.acknowledgedAt ? 'alert-item--acknowledged' : ''}`}>
            <span className="alert-item-severity">{alert.severity}</span>
            <span className="alert-item-name">{alert.name}</span>
            <span className="alert-item-detail">{describeAlert(alert)}</span>
            {alert.firedAt && <span className="alert-item-since" title={new Date(alert.firedAt).toLocaleString()}>since {new Date(alert.firedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
            {!compact && user && (
              alert.acknowledgedAt
                ? <span className="alert-item-ack">acknowledged</span>
                : <button type="button" className="btn-secondary-small" disabled={busy === alert.id} onClick={() => acknowledge(alert)}>Acknowledge</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AlertBanner;
